import React, { useEffect, useRef, useState } from 'react';
import './FranchiseIndustry.css';
import growFacilityImage from '../../Franchise_Page_Images/2-Franchise-ourstory-01.png';

// Single digit roller component for gas pump effect
const DigitRoller: React.FC<{
  targetDigit: number;
  isAnimating: boolean;
  delay: number;
  duration: number;
}> = ({ targetDigit, isAnimating, delay, duration }) => {
  // Create extended strip that spins multiple times before landing on target
  // This creates the "slot machine" effect of spinning through numbers
  const spins = 2; // Number of full rotations before landing
  const targetPosition = spins * 10 + targetDigit; // Final position (20-29)
  // Use em units for precise positioning (each digit is 1em tall)
  const offsetEm = targetPosition;

  return (
    <span className="franchise-industry__digit-roller">
      <span
        className={`franchise-industry__digit-strip ${isAnimating ? 'franchise-industry__digit-strip--animating' : ''}`}
        style={{
          '--target-offset': `-${offsetEm}em`,
          '--animation-delay': `${delay}s`,
          '--animation-duration': `${duration}s`,
        } as React.CSSProperties}
      >
        {/* Create multiple sets of 0-9 for spinning effect, plus final landing digits */}
        {[...Array(spins + 1)].map((_, spinIndex) => (
          <React.Fragment key={spinIndex}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <span key={`${spinIndex}-${n}`} className="franchise-industry__digit-value">{n}</span>
            ))}
          </React.Fragment>
        ))}
      </span>
    </span>
  );
};

// Static character component
const StaticChar: React.FC<{ char: string }> = ({ char }) => (
  <span className="franchise-industry__digit-static">{char}</span>
);

// Animated counter component
const AnimatedStat: React.FC<{
  displayText: string;
  duration: number;
  isVisible: boolean;
}> = ({ displayText, duration, isVisible }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible && !isAnimating) {
      // Small delay before starting animation
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isAnimating]);

  // Parse the display text and create rollers for digits, static for others
  const chars = displayText.split('');
  let digitIndex = 0;

  return (
    <span className="franchise-industry__stat-number franchise-industry__stat-number--animated">
      {chars.map((char, index) => {
        if (/\d/.test(char)) {
          const targetDigit = parseInt(char, 10);
          const currentDigitIndex = digitIndex;
          digitIndex++;
          return (
            <DigitRoller
              key={index}
              targetDigit={targetDigit}
              isAnimating={isAnimating}
              delay={currentDigitIndex * 0.15}
              duration={duration}
            />
          );
        }
        return <StaticChar key={index} char={char} />;
      })}
    </span>
  );
};

const FranchiseIndustry: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="franchise-industry">
      <div className="franchise-industry__content">
        <h2 className="franchise-industry__title">THE INDUSTRY</h2>
        <div className="franchise-industry__divider" />

        <div className="franchise-industry__stats" ref={statsRef}>
          <div className="franchise-industry__stat">
            <AnimatedStat
              displayText="40"
              duration={2.5}
              isVisible={isVisible}
            />
            <p className="franchise-industry__stat-description">
              Cannabis is medically legal in 40 states and recreationally legal in 24. Those numbers continue to grow each year.
            </p>
          </div>

          <div className="franchise-industry__stat">
            <AnimatedStat
              displayText="$39.9B"
              duration={3}
              isVisible={isVisible}
            />
            <p className="franchise-industry__stat-description">
              The U.S. legal cannabis industry is currently $39.9B dollars and is projected to be $100B by 2030.
            </p>
          </div>

          <div className="franchise-industry__stat">
            <AnimatedStat
              displayText="3.43M"
              duration={3.5}
              isVisible={isVisible}
            />
            <p className="franchise-industry__stat-description">
              There were 3.43 million recreational cannabis users in the United States in 2020. By 2025, this number is set to double to 6.1 million.
            </p>
          </div>
        </div>
      </div>

      <div className="franchise-industry__image-container">
        <img
          src={growFacilityImage}
          alt="Sweetspot Cannabis Grow Facility"
          className="franchise-industry__image"
        />
      </div>
    </section>
  );
};

export default FranchiseIndustry;
